import type { Locale } from "@borealis/layout-express";
import { Trans, useLingui } from "@lingui/react";
import type { CarAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import type { ReactNode } from "react";
import { usesScandinavianMiles } from "src/utils/brand.ts";
import { useAppConfig } from "../../../context/AppConfigProviderContext.tsx";
import { Icon } from "../Icon.tsx";
import { ScreenReaderSeparator } from "../ScreenReaderSeparator.tsx";
import { EvExpandableRow } from "./EvExpandableRow.tsx";
import { EvFaqLinks } from "./EvFaqLinks.tsx";
import { EvInfoCard } from "./EvInfoCard.tsx";

const GREEN_TECHNOLOGY_DEDUCTION_URL =
  "https://www.skatteverket.se/privat/fastigheterochbostad/gronteknik.4.676f4884175c97df4192860.html";

type Props = {
  ad: CarAd;
};

/**
 * Branch key for the ICU `select` in the messages below. Must be a string: `<Trans>` turns any
 * non-string, non-number value into a component placeholder, so a boolean would always fall
 * through to `other`.
 */
type UsesMiles = "true" | "false";

/** Running costs always get two decimals, so a rate does not read as "4 kr/mil" next to "3,75 kr/mil". */
const formatRate = (amount: number | undefined, locale: Locale) => {
  const minimumFractionDigits = Number.isInteger(amount) ? 0 : 2;
  return formatNumber(amount, locale, { minimumFractionDigits });
};

export const ChargingCostCard = ({ ad }: Props) => {
  const { i18n } = useLingui();
  const { locale, brand } = useAppConfig();

  const chargingCosts = ad.charging_costs;

  if (!chargingCosts) {
    return null;
  }

  const usesMiles: UsesMiles = usesScandinavianMiles(brand) ? "true" : "false";

  return (
    <EvInfoCard
      testId="ev-charging-cost"
      title={i18n.t({
        id: "itemPageLayout.ev.chargingCost.title",
        message: "Charging",
        comment: "Heading of the charging cost card on electric car ads",
      })}
    >
      <div className="mb-24 border-t [&>div]:border-b">
        <EvExpandableRow
          title={i18n.t({
            id: "itemPageLayout.ev.chargingCost.chargingCost.title",
            message: "Charging cost",
            comment: "Heading of the charging cost row on electric car ads",
          })}
          value={i18n.t({
            id: "itemPageLayout.ev.chargingCost.chargingCost.value",
            message: "{usesMiles, select, true {from {cost_pr_mile} kr/mil} other {from {cost_pr_km} kr/km}}",
            comment:
              "Charging cost value on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA).",
            values: {
              usesMiles,
              cost_pr_mile: formatRate(chargingCosts.home_charging.cost_pr_scandinavian_mile, locale),
              cost_pr_km: formatRate(chargingCosts.home_charging.cost_pr_km, locale),
            },
          })}
          body={<ChargingCostRowBody chargingCosts={chargingCosts} usesMiles={usesMiles} />}
          trackingName="chargingCost"
        />
        <EvExpandableRow
          title={i18n.t({
            id: "itemPageLayout.ev.chargingCost.homeChargingSetup.title",
            message: "Home charging setup",
            comment: "Heading of the home charging setup row on electric car ads",
          })}
          body={<HomeChargingSetupRowBody usesMiles={usesMiles} />}
          trackingName="homeCharging"
        />
      </div>
      <EvFaqLinks links={ad.ev_info_links?.charging} />
    </EvInfoCard>
  );
};

const ChargingCostRowBody = ({
  chargingCosts,
  usesMiles,
}: {
  chargingCosts: CarAd["charging_costs"];
  usesMiles: UsesMiles;
}) => {
  const { locale } = useAppConfig();

  if (!chargingCosts) {
    return null;
  }

  const carKmPrKWh = formatNumber(chargingCosts.range_pr_kwh, locale);

  const homePricePrKWh = formatRate(chargingCosts.home_charging.price_pr_kwh, locale);
  const homeCostPrKm = formatRate(chargingCosts.home_charging.cost_pr_km, locale);
  const homeCostPrMile = formatRate(chargingCosts.home_charging.cost_pr_scandinavian_mile, locale);

  const kwhPrMile = formatNumber(chargingCosts.kwh_pr_scandinavian_mile, locale);

  return (
    <>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body"
          message="Driving an electric car is generally cheaper than driving a gas-powered car — especially if you're able to charge at home."
          comment="Body of the charging cost row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.title"
          message="Home charging"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        {chargingCosts.source === "AD_SPECIFIC" ? (
          <Trans
            id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.adSpecific"
            message={
              "At home, you pay your normal electricity price per kWh.\n {usesMiles, select, true {This car uses {kwh_pr_mile} kWh per mil, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_mile} kr/mil</0>.} other {This car drives {car_km_pr_kWh} km per kWh, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_km} kr/km</0>.}}"
            }
            comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
            values={{
              usesMiles,
              car_km_pr_kWh: carKmPrKWh,
              kwh_pr_mile: kwhPrMile,
              example_price: homePricePrKWh,
              cost_pr_km: homeCostPrKm,
              cost_pr_mile: homeCostPrMile,
            }}
            components={{ 0: <strong /> }}
          />
        ) : (
          <Trans
            id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.example"
            message={
              "At home, you pay your normal electricity price per kWh.\n {usesMiles, select, true {A typical electric car uses about {kwh_pr_mile} kWh per mil, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_mile} kr/mil</0>.} other {A typical electric car drives about {car_km_pr_kWh} km per kWh, so at an example price of {example_price} kr per kWh, you would pay <0>{cost_pr_km} kr/km</0>.}}"
            }
            comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
            values={{
              usesMiles,
              car_km_pr_kWh: carKmPrKWh,
              kwh_pr_mile: kwhPrMile,
              example_price: homePricePrKWh,
              cost_pr_km: homeCostPrKm,
              cost_pr_mile: homeCostPrMile,
            }}
            components={{ 0: <strong /> }}
          />
        )}
      </p>
      <br />
      <p className="text-xs">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.homeCharging.note"
          message="{usesMiles, select, true {Price per mil = kWh/mil × Price/kWh} other {Price per km = (1 ÷ km/kWh) × Price/kWh}}"
          comment="Formula note in the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.publicCharging.title"
          message="Public charging"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.publicCharging.body"
          message="Public rapid chargers add a markup, so they cost more — often around {example_price} kr per kWh. {usesMiles, select, true {At {kwh_pr_mile} kWh per mil, you would pay <0>{cost_pr_mile} kr/mil</0>.} other {At {car_km_pr_kWh} km per kWh, you would pay <0>{cost_pr_km} kr/km</0>.}} Some networks offer lower rates with a membership."
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            example_price: formatRate(chargingCosts.public_charging.price_pr_kwh, locale),
            car_km_pr_kWh: carKmPrKWh,
            kwh_pr_mile: kwhPrMile,
            cost_pr_km: formatRate(chargingCosts.public_charging.cost_pr_km, locale),
            cost_pr_mile: formatRate(chargingCosts.public_charging.cost_pr_scandinavian_mile, locale),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.title"
          message="Gas-powered car"
          comment="Sub heading in the charging cost row on electric car ads"
        />
      </p>
      <p className="whitespace-pre-line">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.body"
          message="{usesMiles, select, true {A gas-powered car using {l_pr_mile} L/mil, with an example price of {example_price} kr/L, costs <0>{cost_pr_mile} kr/mil</0>.} other {A gas-powered car doing {km_pr_l} km/L, with an example price of {example_price} kr/L, costs <0>{cost_pr_km} kr/km</0>.}}"
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            km_pr_l: formatNumber(chargingCosts.petrol_comparison.km_pr_liter, locale),
            l_pr_mile: formatNumber(chargingCosts.petrol_comparison.liters_pr_scandinavian_mile, locale),
            example_price: formatRate(chargingCosts.petrol_comparison.price_pr_liter, locale),
            cost_pr_km: formatRate(chargingCosts.petrol_comparison.cost_pr_km, locale),
            cost_pr_mile: formatRate(chargingCosts.petrol_comparison.cost_pr_scandinavian_mile, locale),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
      <br />
      <p className="text-xs">
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.gasPoweredCar.note"
          message="{usesMiles, select, true {Price per mil = L/mil × Gas price/L} other {Price per km = (1 ÷ km/L) × Gas price/L}}"
          comment="Formula note in the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans id="itemPageLayout.ev.chargingCost.chargingCost.body.savings.title" message="Savings per month" />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.chargingCost.body.savings.body"
          message="{usesMiles, select, true {Home charging costs about {saving_pr_mile} kr per mil less than gas. For someone driving {annual_mile} mil a year, home charging saves you roughly <0>{monthly_amount} kr a month</0> versus gas.} other {Home charging costs about {saving_pr_km} kr per km less than gas. For someone driving {annual_km} km a year, home charging saves you roughly <0>{monthly_amount} kr a month</0> versus gas.}} Charging on public networks will reduce savings."
          comment="Body of the charging cost row on electric car ads. 'usesMiles' is true on the brands that quote running costs per Scandinavian mil (FINN, Blocket) and false where they are per km (DBA). <0> makes the cost bold."
          values={{
            usesMiles,
            saving_pr_km: formatRate(chargingCosts.monthly_savings.saving_pr_km, locale),
            saving_pr_mile: formatRate(chargingCosts.monthly_savings.saving_pr_scandinavian_mile, locale),
            annual_km: formatNumber(chargingCosts.monthly_savings.annual_km, locale),
            annual_mile: formatNumber(chargingCosts.monthly_savings.annual_scandinavian_mile, locale),
            monthly_amount: formatNumber(chargingCosts.monthly_savings.monthly_amount, locale, {
              maximumFractionDigits: 0,
            }),
          }}
          components={{ 0: <strong /> }}
        />
      </p>
    </>
  );
};

const HomeChargingSetupRowBody = ({ usesMiles }: { usesMiles: UsesMiles }) => {
  const { brand } = useAppConfig();

  return (
    <>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body"
          message="Most electric car owners charge at home overnight. Here's what that takes, and what it costs."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.whatYouNeed.title"
          message="What you need"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.whatYouNeed.body"
          message="A wallbox (a dedicated home charger), installed by a certified electrician. An ordinary socket is very slow (a full charge can take over a day) and is not recommended for regular use due to safety risks."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.sharedParking.title"
          message="Apartment or shared parking"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.sharedParking.body"
          message="In an apartment or shared parking, you'll need approval from your housing association or landlord first. Many are adding charging, so it's worth asking."
          comment="Body of the home charging setup row on electric car ads"
        />
      </p>
      <br />
      <p className="font-bold">
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.title"
          message="What it costs"
          comment="Sub heading in the home charging setup row on electric car ads"
        />
      </p>
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.body"
          message="A wallbox plus standard installation (usually up to 10-15 meters of cabling) costs around 10,000–20,000 kr. Older wiring or a fuse-box upgrade can add cost."
          comment="Body of the home charging setup row on electric car ads. Note that the 50% tax deduction is only available in Sweden, and should only be in the swedish translation."
        />
      </p>
      <br />
      <p>
        <Trans
          id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.cost.payback"
          message="{usesMiles, select, true {If you drive 1,500 mil a year and charge at home instead of filling up with gas, a wallbox typically pays for itself inside a year.} other {If you drive 15,000 km a year and charge at home instead of filling up with gas, a wallbox typically pays for itself inside a year.}}"
          comment="Body of the home charging setup row on electric car ads. 'usesMiles' is true on the brands that quote distance in Scandinavian mil (FINN, Blocket) and false where it is in km (DBA)."
          values={{ usesMiles }}
        />
      </p>
      {brand === "blocket" && (
        <>
          <br />
          <p>
            <Trans
              id="itemPageLayout.ev.chargingCost.homeChargingSetup.body.greenTechnologyDeduction"
              message="Read more about the current terms and rates for the green technology deduction (grönt avdrag) at <0>skatteverket.se</0>"
              comment="Body of the home charging setup row on electric car ads, only shown on Blocket. <0> is a link to skatteverket.se. Do not translate the name of the deduction in parenthesis, the localised name in the running text is enough."
              components={{ 0: <GreenTechnologyDeductionLink /> }}
            />
          </p>
        </>
      )}
    </>
  );
};

const GreenTechnologyDeductionLink = ({ children }: { children?: ReactNode }) => (
  <a
    href={GREEN_TECHNOLOGY_DEDUCTION_URL}
    target="_blank"
    rel="noopener nofollow"
    className="inline-flex items-center gap-4"
  >
    {children}
    <ScreenReaderSeparator />
    <Icon name="LinkExternal" className="inline-block align-baseline" />
  </a>
);
